"use client";

import React from "react";
import { Sparkles, Compass, Atom, Orbit, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Reveal } from "@/components/cosmic/Reveal";
export default function UniverseCreator() {
  return <section id="create-your-universe" data-nav="dark" className="relative py-16 sm:py-20 lg:py-28 w-full max-w-full overflow-x-clip text-foreground">
      <div className="relative mx-auto max-w-[76rem] w-full px-4 sm:px-6 z-10">
        {/* Subtle section hairline divider */}
        <div className="ql-divider mb-16" aria-hidden="true" />

        {/* Large Glass Banner Layout */}
        <Reveal>
          <div className="ql-glass-strong p-5 sm:p-12 lg:p-16 relative overflow-hidden rounded-2xl sm:rounded-3xl border border-primary/30 w-full max-w-full">
            {/* Ambient decorative glowing halo in banner background */}
            <div aria-hidden="true" className="absolute -top-32 left-1/2 -translate-x-1/2 w-[min(48rem,100%)] h-[24rem] rounded-full bg-primary/15 blur-[100px] pointer-events-none" />

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              {/* Left Column (cols 1-6): Banner narrative and CTA */}
              <div className="lg:col-span-6 flex flex-col items-start text-left">
                <div className="inline-flex items-center gap-2 mb-4 font-mono text-xs font-semibold tracking-widest uppercase text-primary">
                  <Orbit className="size-3.5" />
                  <span>ARCHETYPE GENERATOR</span>
                </div>

                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground leading-[1.12]">
                  Architect Your Hypothetical Cosmos
                </h2>

                <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans max-w-xl">
                  Choose physical constants, dimensional geometries, and elemental distributions to generate and collect your personal Universe Card.
                </p>

                <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
                  <Link to="/CreateYourUniversePage" className="ql-btn w-full sm:w-auto min-h-[44px] justify-center">
                    <span>Create Your Universe</span>
                    <ArrowRight className="size-4 shrink-0" />
                  </Link>

                  <Link to="/YourInfiniteSelvesPage" className="ql-btn-ghost w-full sm:w-auto min-h-[44px] justify-center">
                    <span>Explore Infinite Selves</span>
                  </Link>
                </div>
              </div>

              {/* Right Column (cols 7-12): Collectible Archetype Preview Specimen */}
              <div className="lg:col-span-6 flex justify-center lg:justify-end w-full min-w-0">
                <div className="w-full max-w-md ql-card p-5 sm:p-7 shadow-2xl relative border-primary/40 bg-card/85 text-left min-w-0">
                  {/* Header Telemetry */}
                  <div className="flex flex-wrap items-center justify-between gap-2 pb-4 border-b border-border/80">
                    <div className="flex items-center gap-2">
                      <Orbit className="size-4 text-primary shrink-0 animate-spin [animation-duration:24s]" />
                      <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                        Simulation Ready
                      </span>
                    </div>
                    <span className="inline-flex items-center rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 font-serif text-[0.7rem] font-medium text-primary shrink-0">
                      Parametric Archetype
                    </span>
                  </div>

                  {/* Card Title */}
                  <div className="pt-5 pb-3">
                    <h3 className="font-serif text-lg sm:text-2xl font-bold text-card-foreground flex items-center gap-2 break-words">
                      <span>Cosmos Archetype 108: Quintessence Sea</span>
                    </h3>
                  </div>

                  {/* Native Stats Presentation */}
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-border/50">
                    <div className="rounded-lg bg-muted/60 p-3 border border-border/40">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Compass className="size-3.5 text-primary shrink-0" />
                        <span>Dimensions</span>
                      </div>
                      <p className="mt-1 font-mono text-sm font-semibold text-card-foreground">
                        4D Spacetime
                      </p>
                    </div>

                    <div className="rounded-lg bg-muted/60 p-3 border border-border/40">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Atom className="size-3.5 text-primary shrink-0" />
                        <span>Gravity</span>
                      </div>
                      <p className="mt-1 font-mono text-sm font-semibold text-card-foreground">
                        0.82 G
                      </p>
                    </div>

                    <div className="rounded-lg bg-muted/60 p-3 border border-border/40">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Sparkles className="size-3.5 text-primary shrink-0" />
                        <span>Predominant</span>
                      </div>
                      <p className="mt-1 font-mono text-sm font-semibold text-card-foreground">
                        Radiant Energy
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}